import { ManagementTenant, User } from '@xeetr/types/models'
import { createContext, Dispatch, SetStateAction } from 'react'

export const ActiveUserContext = createContext({
  user: undefined as User | undefined,
  setUser: (_user: User | undefined) => {},
})

interface IActiveTenantContext {
  tenantId: string
  setTenantId: (_tenantID: string) => void
  tenant: ManagementTenant | undefined
  setTenant: Dispatch<SetStateAction<ManagementTenant | undefined>>
}

/**
 * Context for accessing the active tenant data across the application.
 *
 * The tenant data is loaded by `useUser()` hook at the app root level and synced to this context.
 * This provides easy access to tenant information without triggering additional API calls.
 *
 * ## When to use ActiveTenantContext vs useTenant():
 *
 * ✅ **Use ActiveTenantContext when:**
 * - You just need tenant data (id, type, refreshDuration, etc.)
 * - Parent component already loaded tenant via useUser()
 * - You want to avoid unnecessary React Query calls
 * - Examples: useBookings, useCalendar, useAbsences, useCalendarEvents
 *
 * ✅ **Use useTenant() when:**
 * - You need loading/error states (isLoading, isError, error)
 * - You need mutation functions (updateTenantSettings)
 * - You need to trigger refetch
 * - You need optimistic updates
 * - Examples: Settings pages, tenant management UI, useResourceReorder
 *
 * @example
 * ```tsx
 * // Just need tenant data - use context
 * const { tenant, tenantId } = useContext(ActiveTenantContext)
 * if (tenant?.refreshDuration) { ... }
 *
 * // Need mutations or loading states - use hook
 * const { data: tenant, isLoading, updateTenantSettings } = useTenant()
 * ```
 */
export const ActiveTenantContext = createContext<IActiveTenantContext>({
  tenantId: '',
  setTenantId: () => {},
  tenant: undefined,
  setTenant: () => {},
})
